// Token persistence + JWT helpers. Tokens live in localStorage/sessionStorage.
// NOTE (security): localStorage is readable by any XSS. A production hardening step
// is httpOnly cookies or a strict CSP — tracked as a follow-up, not built here.

const ACCESS_TOKEN_KEY = 'sc_access_token';
const REFRESH_TOKEN_KEY = 'sc_refresh_token';
const USER_ID_KEY = 'sc_user_id';
const SESSION_ID_KEY = 'sc_session_id';
const EXPIRES_AT_KEY = 'sc_token_expires_at';
const REMEMBER_KEY = 'sc_remember_me';
const SESSION_MODE_KEY = 'sc_session_mode';
export const SESSION_UPDATED_EVENT = 'scoutcast:session-updated';
let sessionRevision = 0;

export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number; // seconds
  userId?: number | string;
  sessionId?: number | string;
  rememberMe?: boolean;
  sessionMode?: 'legacy' | 'oauth';
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function extractUserIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const id = payload.sub ?? payload.userId ?? payload.id;
  return id != null ? String(id) : null;
}

export function getJwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

export function persistTokens(t: StoredTokens): void {
  if (!t.accessToken || typeof t.accessToken !== 'string') throw new Error('The sign-in service returned an invalid session.');
  const remember = t.rememberMe ?? localStorage.getItem(REMEMBER_KEY) === 'true';
  clearTokens();
  localStorage.setItem(REMEMBER_KEY, String(remember));
  localStorage.setItem(SESSION_MODE_KEY, t.sessionMode ?? 'legacy');
  localStorage.setItem(ACCESS_TOKEN_KEY, t.accessToken);

  if (t.refreshToken && t.sessionMode !== 'oauth') {
    const store = remember ? localStorage : sessionStorage;
    store.setItem(REFRESH_TOKEN_KEY, t.refreshToken);
  }

  const userId = t.userId ?? extractUserIdFromToken(t.accessToken);
  if (userId != null) localStorage.setItem(USER_ID_KEY, String(userId));
  if (t.sessionId != null) localStorage.setItem(SESSION_ID_KEY, String(t.sessionId));

  const expiryMs =
    t.expiresIn != null
      ? Date.now() + t.expiresIn * 1000 - 60_000 // refresh 60s early
      : getJwtExpiryMs(t.accessToken);
  if (expiryMs) localStorage.setItem(EXPIRES_AT_KEY, String(expiryMs));
  window.dispatchEvent(new Event(SESSION_UPDATED_EVENT));
}

export function isOAuthSession(): boolean {
  return localStorage.getItem(SESSION_MODE_KEY) === 'oauth';
}

export function getSessionRevision(): number {
  return sessionRevision;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY) ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getUserId(): string | null {
  return localStorage.getItem(USER_ID_KEY);
}

export function getSessionId(): string | null {
  return localStorage.getItem(SESSION_ID_KEY);
}

export function isTokenExpiringSoon(withinMs = 60_000): boolean {
  const raw = localStorage.getItem(EXPIRES_AT_KEY);
  if (!raw) return false;
  return Date.now() > Number(raw) - withinMs;
}

export function clearTokens(): void {
  sessionRevision++;
  [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_ID_KEY, SESSION_ID_KEY, EXPIRES_AT_KEY, REMEMBER_KEY, SESSION_MODE_KEY].forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}
